import { createAsyncThunk } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import {
  IGetInvoicesQuery,
  IInvoice,
  IRevokeManualPaymentResponse,
  ICancelInvoiceResponse,
  IDeleteInvoiceResponse,
  IProcessRefundRequest,
  IProcessRefundResponse,
} from '../../interface';
import type {
  IStartSessionRequest,
  IEndSessionRequest,
  IActiveSession,
  IEndSessionResponse,
} from '../../interface/paymentSession.interface';
import { IErrorResponse } from '../../interface/main.interface';
import {
  IInvoiceForEdit,
  INextBillingPreviewRequest,
  INextBillingPreviewResponse,
  IUpdateInvoiceRequest,
  IUpdateInvoiceResponse,
} from '../../interface/studentBillingModel';
import { paymentService } from '../../services/payment';
import { paymentSessionService } from '../../services/payment/session';
import { normalizeAxiosError } from '../../utility/axiosErrorHandler';

export const getInvoices = createAsyncThunk<
  IInvoice[],
  IGetInvoicesQuery,
  { rejectValue: IErrorResponse }
>('payment/invoice', async (query, { rejectWithValue }) => {
  try {
    const response = await paymentService.getInvoices(query);
    return response.data.data.invoices;
  } catch (err) {
    const error = err as AxiosError<IErrorResponse>;
    if (!error.response) {
      throw err;
    }
    return rejectWithValue(error.response.data);
  }
});

interface ManualPaymentPayload {
  assign_invoice_id: string;
  data: {
    amount_received: number;
    payment_date?: string;
    reference_note?: string;
  };
}

export const manualPayment = createAsyncThunk<
  any,
  ManualPaymentPayload,
  { rejectValue: IErrorResponse }
>('payment/manual', async ({ assign_invoice_id, data }, { rejectWithValue }) => {
  try {
    const response = await paymentService.manualPayment(assign_invoice_id, data);
    return response.data;
  } catch (err) {
    const error = err as AxiosError<IErrorResponse>;
    if (!error.response) {
      throw err;
    }
    return rejectWithValue(error.response.data);
  }
});

export const revokeManualPayment = createAsyncThunk<
  IRevokeManualPaymentResponse,
  { assign_invoice_id: string; revocation_note: string },
  { rejectValue: IErrorResponse }
>('payment/revoke-manual', async ({ assign_invoice_id, revocation_note }, { rejectWithValue }) => {
  try {
    const response = await paymentService.revokeManualPayment(assign_invoice_id, revocation_note);
    return response.data;
  } catch (err) {
    // The axios interceptor rejects with the response BODY, so `error.response` is always
    // undefined here. normalizeAxiosError reads the status/message off the body itself, which
    // is what surfaces the backend's 403 ("an open cashier session is required") to the user
    // instead of dropping it.
    return rejectWithValue(normalizeAxiosError(err, 'Failed to revoke payment.'));
  }
});

export const cancelInvoice = createAsyncThunk<
  ICancelInvoiceResponse,
  { assign_invoice_id: string; cancellation_reason: string },
  { rejectValue: IErrorResponse }
>(
  'payment/cancel-invoice',
  async ({ assign_invoice_id, cancellation_reason }, { rejectWithValue }) => {
    try {
      const response = await paymentService.cancelInvoice(assign_invoice_id, cancellation_reason);
      return response.data;
    } catch (err) {
      const error = err as AxiosError<IErrorResponse>;
      if (!error.response) {
        throw err;
      }
      return rejectWithValue(error.response.data);
    }
  },
);

export const deleteInvoice = createAsyncThunk<
  IDeleteInvoiceResponse,
  { assign_invoice_id: string; delete_reason: string },
  { rejectValue: IErrorResponse }
>('payment/delete-invoice', async ({ assign_invoice_id, delete_reason }, { rejectWithValue }) => {
  try {
    const response = await paymentService.deleteInvoice(assign_invoice_id, delete_reason);
    return response.data;
  } catch (err) {
    const error = err as AxiosError<IErrorResponse>;
    if (!error.response) {
      throw err;
    }
    return rejectWithValue(error.response.data);
  }
});

/**
 * Get active payment session
 * Called on page mount to check if user has active session
 * Returns null if no active session (handles both 404 and 200 with message)
 */
export const getActiveSession = createAsyncThunk<
  IActiveSession | null,
  void,
  { rejectValue: IErrorResponse }
>('payment/session/getActive', async (_, { rejectWithValue }) => {
  try {
    const response = await paymentSessionService.getActiveSession();

    // Handle backend returning 200 with "No active session" message
    if (response.data.message === 'No active session' || !response.data.data) {
      return null;
    }

    return response.data.data; // Returns IActiveSession or null
  } catch (err) {
    const error = err as AxiosError<IErrorResponse>;

    // 404 is expected when no active session exists
    if (error.response?.status === 404) {
      return null; // Return null instead of rejecting
    }

    if (!error.response) {
      throw err;
    }
    return rejectWithValue(error.response.data);
  }
});

/**
 * Start new payment session
 * Creates session with opening balance and optional notes
 */
export const startSession = createAsyncThunk<
  IActiveSession,
  IStartSessionRequest,
  { rejectValue: IErrorResponse }
>('payment/session/start', async (data, { rejectWithValue }) => {
  try {
    const response = await paymentSessionService.startSession(data);
    return response.data.data;
  } catch (err) {
    const error = err as AxiosError<IErrorResponse>;
    if (!error.response) {
      throw err;
    }
    return rejectWithValue(error.response.data);
  }
});

/**
 * End active payment session
 * Closes session with closing balance and calculates variance
 */
export const endSession = createAsyncThunk<
  IEndSessionResponse['data'],
  { sessionId: string; data: IEndSessionRequest },
  { rejectValue: IErrorResponse }
>('payment/session/end', async ({ sessionId, data }, { rejectWithValue }) => {
  try {
    const response = await paymentSessionService.endSession(sessionId, data);
    return response.data.data;
  } catch (err) {
    const error = err as AxiosError<IErrorResponse>;
    if (!error.response) {
      throw err;
    }
    return rejectWithValue(error.response.data);
  }
});

export const processRefund = createAsyncThunk<
  IProcessRefundResponse,
  { assign_invoice_id: string; data: IProcessRefundRequest },
  { rejectValue: IErrorResponse }
>('payment/refund', async ({ assign_invoice_id, data }, { rejectWithValue }) => {
  try {
    const response = await paymentService.processRefund(assign_invoice_id, data);
    return response.data;
  } catch (err) {
    const error = err as AxiosError<IErrorResponse>;
    if (!error.response) {
      throw err;
    }
    return rejectWithValue(error.response.data);
  }
});

/**
 * Fetch a single invoice by id (any lifecycle status).
 * Used by the View modal and as the prefill source for the Edit form.
 */
export const getInvoiceById = createAsyncThunk<
  IInvoiceForEdit,
  string,
  { rejectValue: IErrorResponse }
>('payment/invoice/getById', async (invoiceId, { rejectWithValue }) => {
  try {
    const response = await paymentService.getInvoiceById(invoiceId);
    return response.data.data;
  } catch (err) {
    const error = err as AxiosError<IErrorResponse>;
    if (!error.response) {
      throw err;
    }
    return rejectWithValue(error.response.data);
  }
});

/**
 * Preview the next billing date (+ date-related validation verdicts) that the
 * given pending edits WOULD produce, without persisting. The Edit form renders
 * this authoritative value instead of recomputing the date on the client.
 */
export const previewNextBilling = createAsyncThunk<
  INextBillingPreviewResponse,
  { invoiceId: string; data: INextBillingPreviewRequest },
  { rejectValue: IErrorResponse }
>('payment/invoice/previewNextBilling', async ({ invoiceId, data }, { rejectWithValue }) => {
  try {
    const response = await paymentService.previewNextBilling(invoiceId, data);
    return response.data.data;
  } catch (err) {
    const error = err as AxiosError<IErrorResponse>;
    if (!error.response) {
      throw err;
    }
    return rejectWithValue(error.response.data);
  }
});

/**
 * Update an existing invoice (lifecycle + scenario aware).
 * BE may return a `warning` field on the response payload describing
 * time-sensitive scenarios (e.g. today's cycle is already processing).
 */
export const updateInvoice = createAsyncThunk<
  IUpdateInvoiceResponse['data'],
  { invoiceId: string; data: IUpdateInvoiceRequest },
  { rejectValue: IErrorResponse }
>('payment/invoice/update', async ({ invoiceId, data }, { rejectWithValue }) => {
  try {
    const response = await paymentService.updateInvoice(invoiceId, data);
    return response.data.data;
  } catch (err) {
    const error = err as AxiosError<IErrorResponse>;
    if (!error.response) {
      throw err;
    }
    return rejectWithValue(error.response.data);
  }
});

export const paymentActions = {
  getInvoices,
  getInvoiceById,
  updateInvoice,
  manualPayment,
  revokeManualPayment,
  cancelInvoice,
  deleteInvoice,
  processRefund,
  // Session actions
  getActiveSession,
  startSession,
  endSession,
};
